/**
 * Signed distance fields for sculpting: negative inside, roughly the distance to the surface outside. Each shape
 * carries a bounding sphere (centre and radius) that encloses everywhere it can be negative, so unions and cuts can
 * skip shapes that are too far away to matter. Everything is plain arithmetic on numbers: fields are sampled
 * hundreds of thousands of times per mesh.
 */
export interface Shape {
  field(x: number, y: number, z: number): number; cx: number; cy: number; cz: number; r: number;
  /** A lower bound on the field at a point, when the bounding sphere alone is too loose. */
  reach?(x: number, y: number, z: number): number;
}
type Vec = readonly [number, number, number];

/** Polynomial smooth minimum: the union of two surfaces with a fillet of size `k`. */
export function smin(a: number, b: number, k: number) {
  if (k <= 0) return a < b ? a : b;
  const h = Math.max(k - Math.abs(a - b), 0) / k;
  return (a < b ? a : b) - h * h * k * .25;
}
export const smax = (a: number, b: number, k: number) => -smin(-a, -b, k);

/** Rotation from Euler angles (x, then y, then z), as a row-major 3×3 matrix taking local to world. */
function rotation([ax, ay, az]: Vec) {
  const [cx, sx, cy, sy, cz, sz] = [Math.cos(ax), Math.sin(ax), Math.cos(ay), Math.sin(ay), Math.cos(az), Math.sin(az)];
  // R = Rz · Ry · Rx
  return [cz * cy, cz * sy * sx - sz * cx, cz * sy * cx + sz * sx,
    sz * cy, sz * sy * sx + cz * cx, sz * sy * cx - cz * sx,
    -sy, cy * sx, cy * cx];
}

export function sphere([cx, cy, cz]: Vec, r: number): Shape {
  return { cx, cy, cz, r, field: (x, y, z) => Math.sqrt((x - cx) ** 2 + (y - cy) ** 2 + (z - cz) ** 2) - r };
}

/** An ellipsoid with radii along its own axes, optionally turned by Euler angles. */
export function ellipsoid([cx, cy, cz]: Vec, [rx, ry, rz]: Vec, turn?: Vec): Shape {
  const m = turn ? rotation(turn) : undefined, ix = 1 / rx, iy = 1 / ry, iz = 1 / rz, smallest = Math.min(rx, ry, rz);
  return { cx, cy, cz, r: Math.max(rx, ry, rz), field(x, y, z) {
    let px = x - cx, py = y - cy, pz = z - cz;
    // World to local is the transpose.
    if (m) { const lx = m[0] * px + m[3] * py + m[6] * pz, ly = m[1] * px + m[4] * py + m[7] * pz; pz = m[2] * px + m[5] * py + m[8] * pz; px = lx; py = ly; }
    const ax = px * ix, ay = py * iy, az = pz * iz, k0 = Math.sqrt(ax * ax + ay * ay + az * az);
    const bx = ax * ix, by = ay * iy, bz = az * iz, k1 = Math.sqrt(bx * bx + by * by + bz * bz);
    return k1 > 1e-12 ? k0 * (k0 - 1) / k1 : -smallest;
  } };
}

/** A cone between two spheres: a capsule when both radii are equal. */
export function roundCone(a: Vec, b: Vec, r1: number, r2: number): Shape {
  const [ax, ay, az] = a, bax = b[0] - ax, bay = b[1] - ay, baz = b[2] - az;
  const l2 = bax * bax + bay * bay + baz * baz, rr = r1 - r2, a2 = l2 - rr * rr, il2 = 1 / l2;
  return { cx: (ax + b[0]) / 2, cy: (ay + b[1]) / 2, cz: (az + b[2]) / 2, r: Math.sqrt(l2) / 2 + Math.max(r1, r2), field(x, y, z) {
    const pax = x - ax, pay = y - ay, paz = z - az, py = pax * bax + pay * bay + paz * baz, pz = py - l2;
    const qx = pax * l2 - bax * py, qy = pay * l2 - bay * py, qz = paz * l2 - baz * py;
    const x2 = qx * qx + qy * qy + qz * qz, y2 = py * py * l2, z2 = pz * pz * l2, k = Math.sign(rr) * rr * rr * x2;
    if (Math.sign(pz) * a2 * z2 > k) return Math.sqrt(x2 + z2) * il2 - r2;
    if (Math.sign(py) * a2 * y2 < k) return Math.sqrt(x2 + y2) * il2 - r1;
    return (Math.sqrt(x2 * a2 * il2) + py * rr) * il2 - r1;
  } };
}

/** A ring of tube radius `r` about `axis` through `c`, its centreline `big` from the axis: a band, a tie or a hoop. */
export function torus([cx, cy, cz]: Vec, axis: Vec, big: number, r: number): Shape {
  const length = Math.hypot(axis[0], axis[1], axis[2]), nx = axis[0] / length, ny = axis[1] / length, nz = axis[2] / length;
  return { cx, cy, cz, r: big + r, field(x, y, z) {
    const px = x - cx, py = y - cy, pz = z - cz, along = px * nx + py * ny + pz * nz;
    const across = Math.sqrt(Math.max(0, px * px + py * py + pz * pz - along * along));
    return Math.sqrt((across - big) ** 2 + along * along) - r;
  } };
}

/** A smooth tube through several points, one round cone per segment. */
export function chain(points: readonly Vec[], radii: readonly number[], k = 0): Shape {
  return union(k, ...points.slice(1).map((point, i) => roundCone(points[i], point, radii[i], radii[i + 1])));
}

/** A sphere enclosing every shape, grown by the blend. */
function enclose(shapes: readonly Shape[], grow: number) {
  const n = shapes.length, cx = shapes.reduce((s, b) => s + b.cx, 0) / n, cy = shapes.reduce((s, b) => s + b.cy, 0) / n, cz = shapes.reduce((s, b) => s + b.cz, 0) / n;
  const r = Math.max(...shapes.map(b => Math.sqrt((b.cx - cx) ** 2 + (b.cy - cy) ** 2 + (b.cz - cz) ** 2) + b.r));
  return { cx, cy, cz, r: r + grow };
}
const reach = (s: Shape, x: number, y: number, z: number) => s.reach ? s.reach(x, y, z) : Math.sqrt((x - s.cx) ** 2 + (y - s.cy) ** 2 + (z - s.cz) ** 2) - s.r;
/** Compare the spherical lower bound without taking a square root for every rejected shape. */
function reaches(s: Shape, x: number, y: number, z: number, limit: number) {
  if (s.reach) return s.reach(x, y, z) <= limit;
  const radius = s.r + limit;
  return radius >= 0 && (x - s.cx) ** 2 + (y - s.cy) ** 2 + (z - s.cz) ** 2 <= radius * radius;
}

/** Shapes blended into one with fillets of size `k`; shapes too far away to change the result are not sampled. */
export function union(k: number, ...shapes: Shape[]): Shape {
  if (shapes.length === 1) return shapes[0];
  return { ...enclose(shapes, k * .25), field(x, y, z) {
    let d = Infinity;
    for (const shape of shapes) if (reaches(shape, x, y, z, d + k)) d = smin(d, shape.field(x, y, z), k);
    return d;
  } };
}
/** `base` with `cut` carved out of it, the edge rounded by `k`. */
export function subtract(base: Shape, cut: Shape, k = 0): Shape {
  return { cx: base.cx, cy: base.cy, cz: base.cz, r: base.r, field(x, y, z) {
    const d = base.field(x, y, z);
    return reaches(cut, x, y, z, k - d) ? smax(d, -cut.field(x, y, z), k) : d;
  } };
}
/**
 * A shape on the right (x > 0) repeated on the left by folding x. One evaluation serves both sides; use it only for
 * shapes that stay clear of the middle, or the fold leaves a crease there.
 */
export function paired(right: Shape): Shape {
  return { cx: 0, cy: right.cy, cz: right.cz, r: Math.abs(right.cx) + right.r, field: (x, y, z) => right.field(Math.abs(x), y, z), reach: (x, y, z) => reach(right, Math.abs(x), y, z) };
}
